import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Combobox, DateInput } from '../../../components/common';
import { apiError } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { currencyBalanceReportService, currencyService } from '../../../services/resources';
import { sortByLabel } from '../../../config/columns';

const fmt = (n) =>
  n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + '01';

export default function CurrencyBalanceReportPage() {
  const { can } = useAuth();
  const canView = can('REPORT_CURRENCY_BALANCE', 'CanView');

  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [currencyId, setCurrencyId] = useState('');
  const [currencies, setCurrencies] = useState([]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    currencyService.list().then((rows) => {
      const nonIdr = rows.filter((c) => c.currency_code !== 'IDR');
      setCurrencies(nonIdr);
      if (nonIdr.length > 0 && !currencyId) setCurrencyId(nonIdr[0].id);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const filters = { dateFrom, dateTo, currency_id: currencyId };

  const load = async () => {
    if (!currencyId) return;
    setLoading(true);
    setError('');
    try {
      setData(await currencyBalanceReportService.list(filters));
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView && currencyId) load();
    // eslint-disable-next-line
  }, [currencyId]);

  const doExport = async () => {
    setExporting(true);
    try {
      await currencyBalanceReportService.export(filters);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setExporting(false);
    }
  };

  if (!canView) {
    return (
      <Card title="Currency Balance Report">
        <p className="text-sm text-gray-500">You do not have permission to view this page.</p>
      </Card>
    );
  }

  const columns = [
    { key: 'date', label: 'Date' },
    { key: 'doc_no', label: 'Doc No' },
    { key: 'customer_name', label: 'Customer' },
    { key: 'kurs', label: 'Kurs', render: (r) => fmt(r.kurs), align: 'right' },
    { key: 'buy', label: 'Buy', render: (r) => (r.buy ? fmt(r.buy) : ''), align: 'right' },
    { key: 'sell', label: 'Sell', render: (r) => (r.sell ? fmt(r.sell) : ''), align: 'right' },
    { key: 'balance', label: 'Balance', render: (r) => fmt(r.balance), align: 'right' },
  ];

  return (
    <div className="space-y-4">
      <Card title="Currency Balance Report">
        <div className="flex flex-wrap items-end gap-3">
          <DateInput label="From" value={dateFrom} onChange={setDateFrom} />
          <DateInput label="To" value={dateTo} onChange={setDateTo} />
          <div className="w-56">
            <Combobox
              label="Currency"
              value={currencyId}
              onChange={setCurrencyId}
              required
              options={sortByLabel(currencies.map((c) => ({ value: c.id, label: c.currency_code })))}
            />
          </div>
          <Button onClick={load} disabled={loading || !currencyId}>
            {loading ? 'Loading…' : 'Apply'}
          </Button>
          <Button variant="secondary" onClick={doExport} disabled={exporting || !currencyId}>
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </Card>

      {data && (
        <Card>
          <div className="mb-3 flex flex-wrap gap-6 text-sm text-gray-600">
            <span>Opening Balance: <strong>{fmt(data.openingBalance)}</strong></span>
            <span>Closing Balance: <strong>{fmt(data.closingBalance)}</strong></span>
            <span>Total Buy: <strong>{fmt(data.totalBuy)}</strong></span>
            <span>Total Sell: <strong>{fmt(data.totalSell)}</strong></span>
          </div>
          <Table columns={columns} rows={data.rows} loading={loading} maxHeight="60vh" />
        </Card>
      )}
    </div>
  );
}
